import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function PageAutoRevive({ navigate }) {
  return (
    <div>
      <p className="text-sm text-brand-400 font-medium mb-2">Autopilot</p>
      <h1 className="font-display text-3xl font-bold text-white tracking-tight mb-3">Revive</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Dying does not have to end a farm session. Auto Revive notices the moment your character
        goes down, gets it back up with the options you allowed, and the route carries on from where
        it stopped. Everything on this page is on the{' '}
        <strong className="text-white">AUTOPILOT &rsaquo; Revive</strong> tab.
      </p>

      <DocHeading>Set It Up</DocHeading>
      <Step n={1} title="Turn on Revive after death">
        This is the main switch in the <strong>AUTO REVIVE</strong> card. While it is on, a green dot
        shows next to <strong>AUTOPILOT</strong> in the top bar and next to <strong>Revive</strong>{' '}
        in the row of sub-tabs.
      </Step>
      <Step n={2} title="Choose what it may use">
        Open <strong>Revive with</strong> and tick the ways the bot is allowed to get you up. Out of
        the box <strong>Map start</strong> and <strong>Town</strong> are ticked, and{' '}
        <strong>Where I died (costs)</strong> is not.
      </Step>
      <Step n={3} title="That is all">
        It works with or without a running route. If a route is running, it waits while you are
        down and moves on by itself once you are back up &mdash; you do not have to press START
        again.
      </Step>

      <Shot
        name="auto-revive-01-revive-tab.png"
        tab="AUTOPILOT › Revive"
        caption="The Revive tab: your settings on the left, what happened on the right."
        shoot="Az AUTOPILOT › Revive fül futó route mellett, legalább egy halál után — bal oldalt az AUTO REVIVE kártya bekapcsolt „Revive after death” kapcsolóval és a „Revive with” soron a Map start, Town választással (alatta a szürke magyarázó sor); jobb oldalt a LAST DEATHS kártya, amin látszik a State, Died, Revived, Last és Route sor."
      />

      <DocHeading>The Three Options</DocHeading>
      <DocTable
        headers={['Option', 'Where you get up', 'Cost']}
        rows={[
          [<strong className="text-white">Where I died (costs)</strong>, 'Right where you fell, so the route loses no time.', <span className="text-amber-300">Andermant or an item. Off by default.</span>],
          [<strong className="text-white">Map start</strong>, 'At the beginning of the map you died on. The route walks back from there.', 'Depends on the game. If it is not available to you, the bot simply moves on to the next ticked option.'],
          [<strong className="text-white">Town</strong>, 'In town, away from the route (see below).', 'Always free.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        The first request goes out the moment you die. After that the bot asks for the ticked
        options in the order shown, one request a little over a second apart, cycling through
        them until you are standing again. The closed dropdown sums up your choice:{' '}
        <strong>All</strong>, <strong>None</strong>, the names you ticked, or a count such as{' '}
        <strong>2 selected</strong> when the names do not fit.
      </p>

      <Callout type="info">
        <strong>Suggested:</strong> keep the default (Map start + Town). Town is the one option that
        always works, so leaving it ticked means you never stay down. Tick{' '}
        <strong>Where I died (costs)</strong> only if the time saved is worth the andermant to you.
      </Callout>

      <DocHeading>Messages on the Auto Revive Card</DocHeading>
      <DocTable
        headers={['Message', 'What it means']}
        rows={[
          [<span className="text-gray-400">It tries them in this order and stops at the first one that works. These are free (town always is).</span>, 'Normal. Nothing you ticked costs anything.'],
          [<span className="text-amber-300">It tries them in this order and stops at the first one that works. &#39;Where I died&#39; costs andermant or an item.</span>, 'A reminder that the paid option is ticked.'],
          [<span className="text-amber-300">Nothing is ticked - nobody gets up.</span>, 'The switch is on, but every option is unticked. Tick at least one.'],
          [<span className="text-red-400">Disabled: unexpected message layout.</span>, 'The bot noticed something it did not expect (usually after a game update) and switched reviving off to stay safe. Get up by hand until a bot update fixes it.'],
        ]}
      />

      <DocHeading>The Last Deaths Card</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        This card answers the question you open the tab for: <em>did it get me up, and how?</em>
      </p>
      <DocTable
        headers={['Line', 'What it shows']}
        rows={[
          [<strong className="text-white">State</strong>, <><span className="text-emerald-400">alive</span> or <span className="text-red-400">down</span>, live.</>],
          [<strong className="text-white">Died</strong>, 'How many times you died since the bot was loaded, or "not yet".'],
          [<strong className="text-white">Revived</strong>, 'How many times you got back up. It turns orange while it is still 0 after a death.'],
          [<strong className="text-white">Last</strong>, 'When you last got up ("40 s ago", "12 min ago") and the option the bot asked for last. The game does not say which request actually worked, so treat it as the most likely one.'],
          [<strong className="text-white">Route</strong>, <><strong>running</strong>, <strong>held</strong> with the milliseconds left while the route is paused for you, or <strong>not running</strong>.</>],
        ]}
      />

      <DocHeading>Getting Up in Town</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        When you get up anywhere on the route, you see a short <strong>Back on your feet.</strong>{' '}
        notice and the run simply goes on. Town is different: your loaded route belongs to other
        maps, so the route has nothing to follow there. It does not stop, but it waits, and the bot
        warns you:
      </p>
      <Callout type="warning">
        <strong>&ldquo;Up in town - the route is on another map and will wait. Go back or restart
        it.&rdquo;</strong> Walk or teleport back to a map of the route and it picks itself up. Or
        press STOP and START on{' '}
        <button onClick={() => navigate('autopilot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">AUTOPILOT &rsaquo; Play</button>:
        if the route starts with a teleport, the Play tab shows{' '}
        <em>elsewhere - it teleports in</em> and START takes you straight back.
      </Callout>

      <DocHeading>Reviving in a Party</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        If <strong>Play as a party</strong> and <strong>Accept resurrect offers</strong> are both on
        on{' '}
        <button onClick={() => navigate('party')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">AUTOPILOT &rsaquo; Party</button>,
        a teammate&rsquo;s resurrection is the better way up: the team stays together and it costs
        you nothing. So when a living teammate who has <strong>Resurrect dead teammates</strong>{' '}
        turned on stands close to you (within the game&rsquo;s 20 m resurrect range, in the same
        map instance), your own revive holds back for up to about 6 seconds to give that offer
        time to arrive. Once an offer is being accepted, it keeps waiting. If no offer comes, the
        options above take over as usual. With nobody in range it does not wait at all.
      </p>
      <Callout type="info">
        Turn Revive after death on in every client of the team. A teammate who is down but getting
        up is waited for; one who will never get up is left behind once the party&rsquo;s patience
        runs out.
      </Callout>

      <DocHeading>Why Doesn&rsquo;t It Work?</DocHeading>
      <DocTable
        headers={['What you see', 'What to do']}
        rows={[
          ['Died goes up, Revived stays at 0 (orange)', 'Check the card: either nothing is ticked, or the red "Disabled" line is showing. If only Map start is ticked and the game does not offer it, you stay down, so tick Town as a fallback.'],
          ['You got up, but the route is not moving', 'You were revived in town. See Getting Up in Town above.'],
          ['It spent andermant', 'Where I died (costs) is ticked. Untick it.'],
          ['Combat stopped while you were down', 'Normal. Fighting and walking pause while you are dead and start again when you are up.'],
        ]}
      />
    </div>
  );
}

export default PageAutoRevive;
